<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<script lang="ts">
  import type {
    FieldGoalEvent,
    GameraTeamReference,
  } from '@statmuse/core/gamera'
  import { range, max, round } from 'lodash-es'
  import { lineScore, plays, quarterClock } from './stores'
  import Play from './play.svelte'
  import NbaFullcourtShots from './nba-fullcourt-shots.svelte'
  import MainPlay from './main-play.svelte'

  export let awayTeam: GameraTeamReference | undefined
  export let homeTeam: GameraTeamReference | undefined
  export let year: number
  export let live = false
  export let renderLastPlayAsMain = false
  export let renderShotChart = false
  export let domain: 'NBA' | 'WNBA' = 'NBA'

  let selectedTab = 0
  let playTab: 'scoring' | 'all' = 'scoring'

  const playTabs = [
    { label: 'Scoring Plays', value: 'scoring' },
    { label: 'All Plays', value: 'all' },
  ]

  let isHalves = domain === 'WNBA' && year < 2006
  let maxPeriods = isHalves ? 2 : 4

  $: selectorWidth = round(
    (1 / (max([maxPeriods, $lineScore.away?.periods?.length]) + 1)) * 100,
    1,
  )

  $: tabs = [
    { label: 'All', value: 0 },
    ...range(
      1,
      (max([maxPeriods, $lineScore.away?.periods?.length]) ?? maxPeriods) + 1,
    ).map((x) => ({
      label: `${
        x <= maxPeriods ? x : x === maxPeriods + 1 ? '' : x - maxPeriods
      }${x > maxPeriods ? 'OT' : isHalves ? 'H' : 'Q'}`,
      value: x,
    })),
  ]

  $: filteredPlays =
    playTab === 'scoring'
      ? $plays.filter(
          (p) => (p.type === 'fieldGoal' || p.type === 'freeThrow') && p.isMade,
        )
      : $plays

  $: events =
    selectedTab === 0
      ? filteredPlays
      : filteredPlays.filter((p) => p.period === selectedTab)

  $: {
    if (live) {
      events = [...events].reverse()
    }
  }

  $: awayFieldGoals = events.filter(
    (e): e is FieldGoalEvent =>
      e.type === 'fieldGoal' && e.teamId === awayTeam?.teamId,
  )
  $: homeFieldGoals = events.filter(
    (e): e is FieldGoalEvent =>
      e.type === 'fieldGoal' && e.teamId === homeTeam?.teamId,
  )
</script>

<div
  class="md:max-w-96 w-full mx-auto relative flex justify-between bg-gray-8 dark:bg-gray-3 rounded-full border border-gray-6 dark:border-gray-3"
>
  <span
    class="absolute h-full bg-teal dark:bg-gray-7 rounded-full transition-transform ease-out"
    style:width={selectorWidth + '%'}
    style:left={selectedTab * selectorWidth + '%'}
  />
  {#each tabs as tab (tab)}
    <div
      class="flex-1 py-1 text-center relative cursor-pointer"
      class:dark:text-gray-2={selectedTab === tab.value}
      class:text-gray-8={selectedTab === tab.value}
      on:click={() => (selectedTab = tab.value)}
    >
      {tab.label}
    </div>
  {/each}
</div>
{#if renderShotChart && (awayFieldGoals.length > 0 || homeFieldGoals.length > 0)}
  <NbaFullcourtShots
    {domain}
    {awayFieldGoals}
    {homeFieldGoals}
    {awayTeam}
    {homeTeam}
  />
{/if}

<div
  class="md:max-w-96 w-full mx-auto relative flex justify-between bg-gray-8 dark:bg-gray-3 rounded-full border border-gray-6 dark:border-gray-3"
>
  <span
    class="absolute h-full bg-teal dark:bg-gray-7 rounded-full transition-transform ease-out"
    style:width={'50%'}
    style:left={playTab === 'scoring' ? '0%' : '50%'}
  />
  {#each playTabs as tab (tab)}
    <div
      class="flex-1 py-1 text-center relative cursor-pointer"
      class:dark:text-gray-2={playTab === tab.value}
      class:text-gray-8={playTab === tab.value}
      on:click={() => (playTab = tab.value)}
    >
      {tab.label}
    </div>
  {/each}
</div>

{#if events.length > 0}
  {#if renderLastPlayAsMain && $quarterClock.quarter === events[0].period}
    {@const [lastPlay, ...restOfPlays] = events}
    <MainPlay play={lastPlay} {awayTeam} {homeTeam} />
    {#each restOfPlays as play (play.eventSequence)}
      <Play {play} {awayTeam} {homeTeam} />
    {/each}
  {:else}
    {#each events as play (play.eventSequence)}
      <Play {play} {awayTeam} {homeTeam} />
    {/each}
  {/if}
{/if}
